import React, { useEffect, useState } from "react";
import { useClients } from "@/context/ClientContext";
import api, { apiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldAlert, Plus, ExternalLink, Check, RotateCcw, Trash2, Clock, KeyRound, CalendarClock } from "lucide-react";

const LEVEL = {
  overdue: { dot: "bg-rose-500", chip: "bg-rose-100 text-rose-700 border-rose-200", ring: "border-l-rose-500" },
  critical: { dot: "bg-rose-500", chip: "bg-rose-100 text-rose-700 border-rose-200", ring: "border-l-rose-500" },
  soon: { dot: "bg-amber-500", chip: "bg-amber-100 text-amber-700 border-amber-200", ring: "border-l-amber-400" },
  ok: { dot: "bg-emerald-500", chip: "bg-emerald-100 text-emerald-700 border-emerald-200", ring: "border-l-emerald-400" },
  unknown: { dot: "bg-slate-300", chip: "bg-slate-100 text-slate-600 border-slate-200", ring: "border-l-slate-300" },
};
const PLATFORMS = ["", "meta", "facebook", "instagram", "linkedin", "tiktok", "youtube"];
const EMPTY = { title: "", platform: "", client_id: "", due_date: "", recurrence_days: "", note: "", action_url: "", action_id: "" };

const countdown = (d) => {
  if (d == null) return "—";
  if (d < 0) return `${Math.abs(d)} Tage überfällig`;
  if (d === 0) return "heute fällig";
  if (d === 1) return "morgen fällig";
  return `in ${d} Tagen`;
};

export default function Fristen() {
  const { clients } = useClients();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, overdue: 0, critical: 0, soon: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/compliance/deadlines"), api.get("/compliance/summary")]);
      setItems(a.data); setSummary(b.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const clientName = (id) => clients.find((c) => c.id === id)?.name;

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (it) => {
    setForm({ title: it.title, platform: it.platform || "", client_id: it.client_id || "",
      due_date: (it.due_date || "").slice(0, 10), recurrence_days: it.recurrence_days || "",
      note: it.note || "", action_url: it.action_url || "", action_id: it.action_id || "" });
    setEditId(it.id); setOpen(true);
  };

  const save = async () => {
    if (!form.title || !form.due_date) { toast.error("Titel und Fälligkeitsdatum sind Pflicht"); return; }
    setBusy(true);
    const payload = {
      title: form.title, platform: form.platform || null, client_id: form.client_id || null,
      due_date: form.due_date, recurrence_days: form.recurrence_days ? Number(form.recurrence_days) : null,
      note: form.note, action_url: form.action_url, action_id: form.action_id,
    };
    try {
      if (editId) await api.patch(`/compliance/deadlines/${editId}`, payload);
      else await api.post("/compliance/deadlines", payload);
      toast.success(editId ? "Frist aktualisiert" : "Frist angelegt");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const markDone = async (it) => {
    try {
      const { data } = await api.post(`/compliance/deadlines/${it.id}/done`);
      toast.success(data.rolled_to ? `Erledigt – nächste Fälligkeit: ${data.rolled_to}` : "Als erledigt markiert");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const reopen = async (it) => { await api.post(`/compliance/deadlines/${it.id}/reopen`); load(); };
  const del = async (it) => {
    if (!window.confirm(`Frist „${it.title}" löschen?`)) return;
    await api.delete(`/compliance/deadlines/${it.id}`); toast.success("Frist gelöscht"); load();
  };

  const stat = (label, val, cls) => (
    <Card className="p-4 rounded-md border-slate-200 shadow-none">
      <div className={`text-3xl font-display font-extrabold ${cls}`}>{val}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </Card>
  );

  return (
    <div>
      <PageHeader overline="Mandantenübergreifend" title="Fristen & Compliance"
        subtitle="Zentraler Kontrollbereich über alle Kunden hinweg: Plattform-Pflichten, Token-Abläufe und wiederkehrende Aufgaben – mit Countdown, damit nichts verpasst wird."
        actions={<Button data-testid="deadline-new-btn" onClick={openNew} className="rounded-full bg-[#0A0A0A] hover:bg-[#212529] font-semibold"><Plus size={15} className="mr-1.5" /> Frist hinzufügen</Button>} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="deadline-summary">
        {stat("Offen gesamt", summary.total, "text-slate-900")}
        {stat("Überfällig", summary.overdue, summary.overdue ? "text-rose-600" : "text-slate-300")}
        {stat("Kritisch (≤7 T.)", summary.critical, summary.critical ? "text-rose-600" : "text-slate-300")}
        {stat("Bald (≤30 T.)", summary.soon, summary.soon ? "text-amber-600" : "text-slate-300")}
      </div>

      {items.length === 0 ? (
        <EmptyState icon={ShieldAlert} title="Keine Fristen" hint="Lege Plattform-Pflichten oder eigene Termine an – Token-Abläufe erscheinen hier automatisch." />
      ) : (
        <div className="space-y-2.5" data-testid="deadline-list">
          {items.map((it) => {
            const lv = LEVEL[it.level] || LEVEL.unknown;
            const done = it.state === "erledigt";
            return (
              <Card key={it.id} data-testid={`deadline-${it.id}`}
                className={`p-4 rounded-md shadow-none border-slate-200 border-l-4 ${done ? "border-l-slate-200 opacity-60" : lv.ring} flex items-start gap-4`}>
                <div className={`mt-1 h-2.5 w-2.5 rounded-full shrink-0 ${done ? "bg-slate-300" : lv.dot}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900 text-[15px]">{it.title}</span>
                    {it.source === "token"
                      ? <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-indigo-100 text-indigo-700"><KeyRound size={11} /> AUTO</span>
                      : it.recurrence_days ? <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600"><RotateCcw size={11} /> jährlich</span> : null}
                    {it.platform_label && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-[#0A0A0A] text-white">{it.platform_label}</span>}
                  </div>
                  <div className="text-sm text-slate-500 mt-1">{it.note}</div>
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-400 flex-wrap">
                    <span className="inline-flex items-center gap-1"><CalendarClock size={13} /> {(it.due_date || "").slice(0, 10) || "—"}</span>
                    {(it.client_name || clientName(it.client_id)) && <span>Kunde: <b className="text-slate-600">{it.client_name || clientName(it.client_id)}</b></span>}
                    {it.action_id && <span className="font-mono">Maßnahme-ID: {it.action_id}</span>}
                    {it.action_url && <a href={it.action_url} target="_blank" rel="noreferrer" data-testid={`deadline-link-${it.id}`} className="inline-flex items-center gap-1 text-slate-700 hover:text-slate-900 underline"><ExternalLink size={12} /> Zum Account</a>}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <span data-testid={`deadline-status-${it.id}`} className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-full border ${done ? "bg-slate-100 text-slate-500 border-slate-200" : lv.chip}`}>
                    <Clock size={11} /> {done ? "erledigt" : countdown(it.days_left)}
                  </span>
                  <div className="flex items-center gap-1">
                    {it.source === "manual" && !done && (
                      <Button size="sm" variant="ghost" data-testid={`deadline-done-${it.id}`} onClick={() => markDone(it)} className="h-7 px-2 text-emerald-700 hover:bg-emerald-50"><Check size={14} className="mr-1" /> Erledigt</Button>
                    )}
                    {it.source === "manual" && done && (
                      <Button size="sm" variant="ghost" onClick={() => reopen(it)} className="h-7 px-2 text-slate-500"><RotateCcw size={14} className="mr-1" /> Öffnen</Button>
                    )}
                    {it.source === "manual" && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(it)} className="h-7 px-2 text-slate-500">Bearb.</Button>
                        <Button size="sm" variant="ghost" data-testid={`deadline-delete-${it.id}`} onClick={() => del(it)} className="h-7 w-7 p-0 text-rose-500 hover:bg-rose-50"><Trash2 size={14} /></Button>
                      </>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="deadline-dialog">
          <DialogHeader><DialogTitle>{editId ? "Frist bearbeiten" : "Neue Frist"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Titel *</Label>
              <Input data-testid="deadline-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="z.B. Meta Data Use Checkup" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Fälligkeitsdatum *</Label>
                <Input type="date" data-testid="deadline-date-input" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Wiederholung (Tage)</Label>
                <Input type="number" data-testid="deadline-recurrence-input" value={form.recurrence_days} onChange={(e) => setForm({ ...form, recurrence_days: e.target.value })} placeholder="365 = jährlich" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Plattform</Label>
                <Select value={form.platform || "__none__"} onValueChange={(v) => setForm({ ...form, platform: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="deadline-platform-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— keine —</SelectItem>
                    {PLATFORMS.filter(Boolean).map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Kunde</Label>
                <Select value={form.client_id || "__none__"} onValueChange={(v) => setForm({ ...form, client_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="deadline-client-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— keiner —</SelectItem>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Notiz / Was ist zu tun?</Label>
              <Textarea data-testid="deadline-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Link zum Account</Label>
                <Input data-testid="deadline-url-input" value={form.action_url} onChange={(e) => setForm({ ...form, action_url: e.target.value })} placeholder="https://…" />
              </div>
              <div>
                <Label className="text-xs">Maßnahme-ID</Label>
                <Input data-testid="deadline-actionid-input" value={form.action_id} onChange={(e) => setForm({ ...form, action_id: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button data-testid="deadline-save-btn" disabled={busy} onClick={save} className="bg-[#0A0A0A] hover:bg-[#212529]">{editId ? "Speichern" : "Anlegen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
